import AppShell from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  classKey,
  getSessionUser,
  getStudentRecordForSession,
  getFeeInstallmentProgress,
  saveFeePaymentForStudent,
  getFeePaymentsForStudent,
} from "@/lib/eisStore";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CircleCheckBig, CircleX, Clock3 } from "lucide-react";
import { checkPaymentStatus } from "@/lib/paymentApi";

function StatusIcon({ status }) {
  if (status === "success") {
    return <CircleCheckBig className="h-14 w-14 text-emerald-600" />;
  }
  if (status === "failed") {
    return <CircleX className="h-14 w-14 text-rose-600" />;
  }
  return <Clock3 className="h-14 w-14 text-amber-500" />;
}

export default function PaymentStatus() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") || "processing").toLowerCase();
  const merchantTxnNo = params.get("txn") || "";
  const studentName = params.get("studentName") || "";
  const amount = params.get("amount") || "";
  const standard = params.get("standard") || "";
  const division = params.get("division") || "";
  const dueDate = params.get("dueDate") || "";
  const responseCode = params.get("responseCode") || "";
  const responseDescription = params.get("responseDescription") || "";
  const user = getSessionUser();
  const studentRecord = getStudentRecordForSession(user) || user || {};
  const studentId = params.get("studentId") || studentRecord.id || user?.id || user?.email || "";
  const classId = standard && division ? classKey(standard, division) : "";
  const [refreshTick, setRefreshTick] = useState(0);
  const progress =
    classId && studentId && standard
      ? getFeeInstallmentProgress(classId, studentId, standard)
      : null;

  useEffect(() => {
    if (status !== "success" || !merchantTxnNo || !classId || !studentId || !amount) {
      return;
    }

    const marker = `eis_payment_processed_${merchantTxnNo}`;
    if (sessionStorage.getItem(marker)) {
      return;
    }

    const alreadyRecorded = getFeePaymentsForStudent(classId, studentId).some((payment) => payment.note === merchantTxnNo);
    if (!alreadyRecorded) {
      saveFeePaymentForStudent(classId, studentId, {
        amount: Number(amount),
        note: merchantTxnNo,
      });
    }
    sessionStorage.setItem(marker, "true");
    setRefreshTick((value) => value + 1);
  }, [amount, classId, merchantTxnNo, studentId, status]);

  const title =
    status === "success"
      ? "Payment successful"
      : status === "failed"
        ? "Payment failed"
        : "Payment processing";

  const description =
    status === "success"
      ? "Your fee payment has been completed successfully."
      : status === "failed"
        ? "The payment did not complete. You can try again from the checkout page."
        : "We are confirming your payment. This page will update once the callback arrives.";
  const failureHelp =
    status === "failed"
      ? responseCode
        ? `Bank declined the transaction${responseCode ? ` (code ${responseCode})` : ""}. Please try another test method or contact ICICI UAT support.`
        : "Bank declined the transaction. Please try another test method or contact ICICI UAT support."
      : "";

  const refreshStatus = async () => {
    if (!merchantTxnNo) {
      toast.error("Missing transaction number");
      return;
    }

    try {
      await checkPaymentStatus({ merchantTxnNo });
      toast.success("Status refreshed");
    } catch (error) {
      toast.error(error?.message || "Unable to refresh status");
    }
  };

  return (
    <AppShell>
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-2xl border-slate-200 shadow-xl">
          <CardContent className="p-6 md:p-8 text-center space-y-6">
            <div className="flex justify-center">
              <StatusIcon status={status} />
            </div>

            <div className="space-y-2">
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">{title}</h1>
              <p className="text-slate-500 max-w-xl mx-auto">{description}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <Info label="Transaction" value={merchantTxnNo || "Not available"} />
              <Info label="Student" value={studentName || studentRecord.studentName || studentRecord.name || "Student"} />
              <Info label="Class" value={standard && division ? `${standard} ${division}` : "Not available"} />
              <Info label="Amount" value={amount ? `₹${Number(amount).toLocaleString("en-IN")}` : "Not available"} />
              <Info label="Due Date" value={dueDate || "Not available"} />
              <Info label="Gateway Code" value={responseCode || "Not available"} />
              <Info label="Gateway Message" value={responseDescription || "Not available"} />
              <Info
                label="Next Due"
                value={
                  progress?.isFullyPaid
                    ? "All paid"
                    : progress?.nextDueDate || dueDate || "Not available"
                }
              />
              <Info label="Status" value={status.toUpperCase()} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="rounded-full bg-[#0b1f7a] text-white hover:bg-[#09185f]"
              >
                Go to Dashboard
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/fees")}
                className="rounded-full"
              >
                Back to Fees
              </Button>
              <Button type="button" variant="ghost" onClick={refreshStatus} className="rounded-full">
                Refresh Status
              </Button>
            </div>

            {status === "success" && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                Payment recorded in the school app.{" "}
                {progress?.nextInstallment
                  ? `Next installment due: ${progress.nextInstallment.label}.`
                  : "All installments are complete."}
              </div>
            )}
            {status === "failed" && responseCode && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
                {failureHelp}
                {responseDescription ? ` ICICI message: ${responseDescription}.` : ""}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900 break-words">{value}</p>
    </div>
  );
}
